import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Spinner, Badge, Form, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { matchApi, formationsApi } from "@/api";
import VideoPlayer from "@/components/VideoPlayer";
import PositionPitch from "./PositionPitch";
import FormationPitchSelector from "./FormationPitchSelector";
import FormationTimeline from "./FormationTimeline";
import "./VerifyPositions.scss";

function useFullscreen(ref) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);
  const toggle = useCallback(() => {
    if (!ref.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      ref.current.requestFullscreen();
    }
  }, [ref]);
  return { isFullscreen, toggle };
}

const POSITIONS = [
  { name: "goalkeeper", sides: ["center"] },
  { name: "center_defender", sides: ["left", "center", "right"] },
  { name: "wing_defender", sides: ["left", "right"] }, // NECESSARY_SIDE
  { name: "defensive_midfielder", sides: ["left", "center", "right"] },
  { name: "center_midfielder", sides: ["left", "center", "right"] },
  { name: "wing_midfielder", sides: ["left", "right"] }, // NECESSARY_SIDE
  { name: "attacking_midfielder", sides: ["left", "center", "right"] },
  { name: "winger", sides: ["left", "right"] }, // NECESSARY_SIDE
  { name: "center_forward", sides: ["left", "center", "right"] },
];

const WINDOW_SEC = 60; // 1 min
const HIGHLIGHT_COLOR = "#f59e0b";
const OTHER_COLOR = "rgba(255,255,255,0.35)";

const POSITION_LINE = {
  goalkeeper: "gk",
  center_defender: "def", wing_defender: "def",
  defensive_midfielder: "dmid",
  center_midfielder: "mid", wing_midfielder: "mid",
  attacking_midfielder: "amf",
  winger: "fwd", center_forward: "fwd",
};

function getAllowedCombos(formationName, catalog) {
  if (!catalog) return null;
  const entry = catalog.find((f) => f.name === formationName);
  if (!entry?.slots?.length) return null;
  const combos = new Set();
  for (const s of entry.slots) {
    combos.add(`${s.name}|${s.side}`);
  }
  return combos;
}

function getLineCounts(formationName, catalog) {
  if (!catalog) return null;
  const entry = catalog.find((f) => f.name === formationName);
  if (!entry?.slots?.length) return null;
  const counts = { gk: 0, def: 0, dmid: 0, mid: 0, amf: 0, fwd: 0 };
  for (const s of entry.slots) {
    const line = POSITION_LINE[s.name];
    if (line) counts[line]++;
  }
  return counts;
}

function formatPos(name, side) {
  if (!name) return "";
  const initials = name
    .split("_")
    .map((w) => w[0].toUpperCase())
    .join("");
  if (!side || side === "center") return initials;
  return side[0].toUpperCase() + "_" + initials;
}

function getPositionAtTimestamp(positions, tsSec, endSec) {
  if (!positions || positions.length === 0) return null;
  let best = null;
  for (const p of positions) {
    if ((p.timestamp ?? 0) <= tsSec) best = p;
  }
  if (best && !best.name) return null;
  if (!best && endSec != null) {
    // Substitute entering mid-window: find first position within [tsSec, endSec)
    let earliest = null;
    for (const p of positions) {
      const ts = p.timestamp ?? 0;
      if (ts > tsSec && ts < endSec && p.name) {
        if (!earliest || ts < (earliest.timestamp ?? 0)) earliest = p;
      }
    }
    return earliest;
  }
  return best;
}

// Backend now sends weighted positions pre-aggregated by 1-min window:
//   weightedPositions[teamKind][windowIdx] = {phase: {uid: [x, y]}}
// One window covers WINDOW_SEC seconds (60s = 1min).
function hasWeightedPosition(weightedPositions, teamKind, uid, startSec, endSec) {
  const teamData = weightedPositions?.[teamKind];
  if (!teamData) return false;
  const uidStr = String(uid);
  const startWin = Math.floor(startSec / WINDOW_SEC);
  const endWin = Math.max(startWin, Math.ceil(endSec / WINDOW_SEC) - 1);
  for (let w = startWin; w <= endWin; w++) {
    const winData = teamData[String(w)] || teamData[w];
    if (!winData) continue;
    for (const phase of ["defense", "attack"]) {
      const coords = (winData[phase] || {})[uidStr];
      if (coords && coords[0] != null && coords[1] != null) return true;
    }
  }
  return false;
}

function buildDynamicWindows(supertracks, getEffPos, formationsTimeline, matchTimeMin) {
  const matchTimeSec = (matchTimeMin || 45) * 60;
  const changeTimes = new Set();
  const formationTimes = new Set();

  for (const st of supertracks) {
    const positions = getEffPos(st.super_track_uid);
    for (const p of positions) {
      const ts = p.timestamp ?? 0;
      if (ts > 0) changeTimes.add(ts);
    }
  }

  if (formationsTimeline) {
    for (const runs of Object.values(formationsTimeline)) {
      if (!Array.isArray(runs)) continue;
      for (const run of runs) {
        if (run.start_sec > 0) {
          changeTimes.add(run.start_sec);
          formationTimes.add(run.start_sec);
        }
      }
    }
  }

  if (changeTimes.size === 0) return [];

  let maxEndSec = matchTimeSec;
  if (formationsTimeline) {
    for (const runs of Object.values(formationsTimeline)) {
      if (!Array.isArray(runs)) continue;
      for (const run of runs) {
        if (run.end_sec > maxEndSec) maxEndSec = run.end_sec;
      }
    }
  }

  const sorted = [...changeTimes].sort((a, b) => a - b);
  // Merge timestamps within 120s of each other (e.g. substitution at min 20,
  // substitute's first data at min 21 should be one window, not two).
  // Formation timeline boundaries are never merged away.
  const MERGE_GAP = 120;
  const merged = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    if (formationTimes.has(sorted[i]) || sorted[i] - merged[merged.length - 1] >= MERGE_GAP) {
      merged.push(sorted[i]);
    }
  }
  const windows = [];
  for (let i = 0; i < merged.length; i++) {
    const endSec = i < merged.length - 1 ? merged[i + 1] : maxEndSec;
    windows.push({
      startSec: merged[i],
      endSec: endSec,
      label: `${Math.floor(merged[i] / 60)}'`,
    });
  }
  return windows;
}

/** Build tracks + colors for VideoPlayer — only the selected supertrack. */
function buildVideoTracks(trackBoxes, superTracks, highlightUid) {
  const tracks = {};
  const colors = {};

  if (!trackBoxes || !superTracks || highlightUid == null) return { tracks, colors };

  const st = superTracks[highlightUid];
  if (!st) return { tracks, colors };

  const trackIds = (st.tracks || st.tracks_data || []).map((t) => t.track_id);
  for (const tid of trackIds) {
    if (trackBoxes[tid]) {
      tracks[tid] = trackBoxes[tid];
      colors[tid] = HIGHLIGHT_COLOR;
    }
  }
  return { tracks, colors };
}

function getActiveFormation(formationsTimeline, teamKind, tsSec) {
  const runs = formationsTimeline?.[teamKind];
  if (!runs || runs.length === 0) return null;
  for (let i = runs.length - 1; i >= 0; i--) {
    if (runs[i].start_sec <= tsSec) return runs[i].formation;
  }
  return runs[0]?.formation || null;
}

const AddFormationModal = ({ show, onHide, onSave, formations, defaultMinute }) => {
  const [minute, setMinute] = useState(defaultMinute || 0);
  const [formation, setFormation] = useState("");

  useEffect(() => {
    setMinute(defaultMinute || 0);
    setFormation("");
  }, [defaultMinute, show]);

  return (
    <Modal show={show} onHide={onHide} centered size="sm">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Add Formation Change</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: 12 }}>Minute</Form.Label>
          <Form.Control
            type="number"
            size="sm"
            value={minute}
            onChange={(e) => setMinute(Number(e.target.value))}
            min={1}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label style={{ fontSize: 12 }}>Formation</Form.Label>
          <Form.Select size="sm" value={formation} onChange={(e) => setFormation(e.target.value)}>
            <option value="">Select...</option>
            {(formations || []).map((f) => (
              <option key={f.name} value={f.name}>{f.display_name || f.name}</option>
            ))}
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={() => onSave(minute, formation)} disabled={!formation || minute < 1}>
          Add
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const PositionEditModal = ({ show, onHide, currentPos, onSave, onClear, formationName, formationsCatalog, takenCombos, lineCounts, startPos, isWindowEdit }) => {
  const [name, setName] = useState("");
  const [side, setSide] = useState("center");

  useEffect(() => {
    if (currentPos) {
      setName(currentPos.name || "");
      setSide(currentPos.side || "center");
    } else {
      setName("");
      setSide("center");
    }
  }, [currentPos]);

  // Allowed (name|side) combos for the formation (only when validating start positions)
  const allowedCombos = formationName ? getAllowedCombos(formationName, formationsCatalog) : null;

  const availablePositions = POSITIONS.filter((p) => {
    const validSides = p.sides.filter((s) => {
      const combo = `${p.name}|${s}`;
      if (allowedCombos && !allowedCombos.has(combo)) return false;
      return true;
    });
    return validSides.length > 0;
  });

  const baseSides = POSITIONS.find((p) => p.name === name)?.sides || ["center"];

  const availableSidesFiltered = baseSides.filter((s) => {
    const combo = `${name}|${s}`;
    if (allowedCombos && !allowedCombos.has(combo)) return false;
    return true;
  });

  // Auto-fix side when it's not available
  useEffect(() => {
    if (side && !availableSidesFiltered.includes(side) && availableSidesFiltered.length > 0) {
      setSide(availableSidesFiltered[0]);
    }
  }, [name, availableSidesFiltered, side]);

  const isValid = name && availableSidesFiltered.includes(side);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Position</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex gap-3">
          <Form.Group className="flex-grow-1">
            <Form.Label>Position</Form.Label>
            <Form.Select value={name} onChange={(e) => setName(e.target.value)}>
              <option value="">—</option>
              {availablePositions.map((p) => (
                <option key={p.name} value={p.name}>
                  {formatPos(p.name, "center")} — {p.name}
                </option>
              ))}
            </Form.Select>
            {formationName && availablePositions.length < POSITIONS.length && (
              <Form.Text className="text-muted" style={{ fontSize: 10 }}>
                Showing only positions valid for the formation
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group style={{ minWidth: 120 }}>
            <Form.Label>Side</Form.Label>
            <Form.Select value={side} onChange={(e) => setSide(e.target.value)}>
              {availableSidesFiltered.length === 0 ? (
                <option value="">All taken</option>
              ) : (
                availableSidesFiltered.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))
              )}
            </Form.Select>
          </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {isWindowEdit && startPos && (
          <Button
            variant="outline-info"
            size="sm"
            onClick={() => {
              setName(startPos.name);
              setSide(startPos.side);
            }}
          >
            Use start position ({formatPos(startPos.name, startPos.side)})
          </Button>
        )}
        {currentPos && onClear && (
          <Button
            variant="outline-danger"
            size="sm"
            className="me-auto"
            onClick={onClear}
            title="Remove this position entry"
          >
            <FontAwesomeIcon icon={["fas", "trash"]} /> Clear
          </Button>
        )}
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => onSave({ name, side })}
          disabled={!isValid}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const PlayerBindModal = ({ show, onHide, players, onBind, onCreate, search, setSearch, loading, supertrackJersey }) => {
  // p = [id, first_name, last_name, jersey, team_id, external_id, isBound]
  const filtered = (players || []).filter((p) => {
    const name = `${p[1]} ${p[2]}`.toLowerCase();
    const jersey = String(p[3] || "");
    return name.includes(search.toLowerCase()) || jersey.includes(search);
  });

  // Detect duplicate jersey numbers within filtered list
  const jerseyCounts = {};
  for (const p of filtered) {
    if (p[3]) {
      jerseyCounts[p[3]] = (jerseyCounts[p[3]] || 0) + 1;
    }
  }

  // Sort: matching jersey first, then free players with jersey, free without jersey, then already bound
  const sorted = [...filtered].sort((a, b) => {
    const aBound = a[6] ? 1 : 0;
    const bBound = b[6] ? 1 : 0;
    if (aBound !== bBound) return aBound - bBound;
    const aJersey = String(a[3] || "");
    const bJersey = String(b[3] || "");
    const targetJersey = String(supertrackJersey || "");
    const aMatch = targetJersey && aJersey === targetJersey ? 1 : 0;
    const bMatch = targetJersey && bJersey === targetJersey ? 1 : 0;
    if (aMatch !== bMatch) return bMatch - aMatch;
    const aHas = aJersey ? 1 : 0;
    const bHas = bJersey ? 1 : 0;
    if (aHas !== bHas) return bHas - aHas;
    return `${a[2]} ${a[1]}`.localeCompare(`${b[2]} ${b[1]}`);
  });

  const freeCount = sorted.filter((p) => !p[6]).length;
  const boundCount = sorted.filter((p) => p[6]).length;

  return (
    <Modal show={show} onHide={onHide} centered size="sm">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Bind Player</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: 400, overflow: "hidden", display: "flex", flexDirection: "column", padding: "8px 16px" }}>
        <Form.Control
          size="sm"
          placeholder="Search by name or jersey..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && search.trim() && sorted.length === 0) {
              onCreate(search);
            }
          }}
          autoFocus
          className="mb-2"
        />
        <div style={{ overflowY: "auto", flex: 1 }}>
          {sorted.length === 0 && !search.trim() && (
            <div className="text-muted text-center py-2" style={{ fontSize: 12 }}>No available players</div>
          )}
          {sorted.length === 0 && search.trim() && (
            <div className="text-muted text-center py-2" style={{ fontSize: 12 }}>
              No match — press Enter or click &quot;Create&quot; below
            </div>
          )}
          {sorted.map((p, idx) => {
            const isBound = p[6];
            const isDuplicate = p[3] && jerseyCounts[p[3]] > 1;
            const hasExternalId = !!p[5];
            const highlight = isDuplicate && hasExternalId && !isBound;
            const jerseyMatches = supertrackJersey && String(p[3]) === String(supertrackJersey);
            const showDivider = isBound && idx === freeCount && boundCount > 0;

            return (
              <div key={p[0]}>
                {showDivider && (
                  <div className="text-muted" style={{ fontSize: 10, padding: "4px 8px", borderTop: "1px solid #e5e7eb", marginTop: 4 }}>
                    Already bound
                  </div>
                )}
                <div
                  className="d-flex align-items-center px-2 py-1"
                  style={{
                    cursor: "pointer",
                    borderRadius: 4,
                    fontSize: 13,
                    opacity: isBound ? 0.55 : 1,
                    background: highlight ? "#dcfce7" : jerseyMatches && !isBound ? "#f0fdf4" : undefined,
                  }}
                  onMouseEnter={(e) => { if (!highlight) e.currentTarget.style.background = isBound ? "#f9fafb" : "#f3f4f6"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = highlight ? "#dcfce7" : jerseyMatches && !isBound ? "#f0fdf4" : ""; }}
                  onClick={() => onBind(p[0], p)}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 28,
                      textAlign: "right",
                      marginRight: 8,
                      fontWeight: 600,
                      fontSize: 11,
                      color: p[3] ? (jerseyMatches && !isBound ? "#16a34a" : "#374151") : "#d1d5db",
                    }}
                  >
                    {p[3] || "—"}
                  </span>
                  <span>{p[2]} {p[1]}</span>
                  {highlight && (
                    <span style={{ marginLeft: "auto", fontSize: 9, color: "#16a34a", fontWeight: 600 }}>ext</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {search.trim() && (
          <Button variant="outline-primary" size="sm" onClick={() => onCreate(search)} disabled={loading}>
            Create &quot;{search}&quot;
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={onHide}>Cancel</Button>
      </Modal.Footer>
    </Modal>
  );
};

const VerifyPositions = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const matchId = videoId;
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(videoContainerRef);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [data, setData] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});
  const [formationLeft, setFormationLeft] = useState("");
  const [formationRight, setFormationRight] = useState("");
  const [playerView, setPlayerView] = useState("formation"); // "formation" | "video"
  const [pitchMode, setPitchMode] = useState("window"); // "window" | "match"
  const [pitchMinute, setPitchMinute] = useState(0);
  const [pitchRange, setPitchRange] = useState(null); // { startSec, endSec } when cell clicked
  const [pitchWindowMin, setPitchWindowMin] = useState(1); // slider window size in minutes (1-10)
  const [pitchPhase, setPitchPhase] = useState("common");
  const [formationsCatalog, setFormationsCatalog] = useState(null);
  const [formationsTimeline, setFormationsTimeline] = useState({});
  const [addFormationModal, setAddFormationModal] = useState(null);

  const resetStartPositions = useCallback((teamKind) => {
    if (!data) return;
    const teamUids = data.supertracks
      .filter((s) => s.kind === teamKind)
      .map((s) => s.super_track_uid);

    setPendingChanges((prev) => {
      const next = { ...prev };
      for (const uid of teamUids) {
        const current = next[uid] ?? data.supertracks.find((s) => s.super_track_uid === uid)?.positions ?? [];
        next[uid] = current.filter((p) => (p.timestamp ?? 0) !== 0);
      }
      return next;
    });
  }, [data]);

  // Video state
  const [match, setMatch] = useState(null);
  const [allTracks, setAllTracks] = useState(null);
  const [superTracksMap, setSuperTracksMap] = useState(null);
  const [fps, setFps] = useState(25);
  const [frameStep, setFrameStep] = useState(1);
  const [selectedCell, setSelectedCell] = useState(null); // { uid, windowIdx }
  const [clipboard, setClipboard] = useState(null);
  const [roleMethod, setRoleMethod] = useState("hungarian");

  // Player binding state
  const [teamPlayers, setTeamPlayers] = useState({});
  const [teamIdMap, setTeamIdMap] = useState({});
  const [bindModal, setBindModal] = useState(null);
  const [playerSearch, setPlayerSearch] = useState("");
  const [bindingUid, setBindingUid] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [posRes, matchRes] = await Promise.all([
        matchApi.getVerifyPositions(matchId),
        matchApi.getMatch(matchId),
      ]);
      setData(posRes);
      setMatch(matchRes);
      setFps(matchRes?.video?.fps || 25);
      setPendingChanges({});
      setPitchRange(null);
      setFormationLeft(posRes.formations?.left_team || "");
      setFormationRight(posRes.formations?.right_team || "");
    } catch (e) {
      toast.error("Failed to load data");
      console.error(e);
    } finally {
      setLoading(false);
    }

    // Lazy-load: only supertracks meta (no bounding boxes) on initial load.
    // Per-supertrack tracks are loaded on demand when user clicks a cell.
    matchApi.getMatchSuperTracksFull(matchId, { meta_only: 1 })
      .then((fullRes) => {
        setSuperTracksMap(fullRes.supertracks || {});
        if (fullRes.frame_step) setFrameStep(fullRes.frame_step);
      })
      .catch(() => {
        // Boxes may not be available — video still works without them
      });
  }, [matchId]);

  // Lazy-load bounding boxes for the selected supertrack
  useEffect(() => {
    if (!selectedCell) return;
    const uid = selectedCell.uid;
    if (!superTracksMap || !superTracksMap[uid]) return;
    // Already loaded for this uid?
    const trackIds = (superTracksMap[uid].tracks || superTracksMap[uid].tracks_data || [])
      .map((t) => t.track_id);
    if (trackIds.length === 0) return;
    if (allTracks && trackIds.every((tid) => tid in allTracks)) return;

    matchApi.getSuperTrackFullByUid(matchId, uid)
      .then((res) => {
        if (res.tracks) {
          setAllTracks((prev) => ({ ...(prev || {}), ...res.tracks }));
        }
      })
      .catch(() => {});
  }, [selectedCell, superTracksMap, matchId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const effectiveTimeMin = data?.video_duration
    ? Math.ceil(data.video_duration / 60)
    : (data?.match_time || 45);

  // Fetch formations catalog on mount
  useEffect(() => {
    const pc = data?.players_per_team || 11;
    formationsApi.getFormations({ players_count: pc })
      .then((resp) => setFormationsCatalog(resp.data || resp))
      .catch(() => {});
  }, [data?.players_per_team]);

  // Initialize formationsTimeline from data
  useEffect(() => {
    if (data?.formations_timeline) {
      setFormationsTimeline({ ...data.formations_timeline });
    }
  }, [data]);

  // Fetch team players for binding
  useEffect(() => {
    if (!data?.activity_id) return;
    matchApi.getBindInfo(data.activity_id)
      .then((bindInfo) => {
        const kindMap = {};
        for (const [teamId, matchOrders] of Object.entries(bindInfo.order)) {
          for (const [mid, , kind] of matchOrders) {
            if (String(mid) === String(matchId)) {
              kindMap[kind] = parseInt(teamId);
            }
          }
        }
        setTeamIdMap(kindMap);
        setTeamPlayers({
          left_team: bindInfo.team_players[kindMap.left_team] || [],
          right_team: bindInfo.team_players[kindMap.right_team] || [],
        });
      })
      .catch((e) => {
        console.warn("Failed to load team players for binding:", e);
      });
  }, [data?.activity_id, matchId]);

  // Seek video when cell is selected
  useEffect(() => {
    if (!selectedCell || !videoRef.current || !data) return;
    const st = data.supertracks.find((s) => s.super_track_uid === selectedCell.uid);
    if (!st) return;
    const getPos = (uid) => pendingChanges[uid] ?? data.supertracks.find((s2) => s2.super_track_uid === uid)?.positions ?? [];
    const teamSts = data.supertracks.filter((s) => s.kind === st.kind);
    const tl = formationsTimeline?.[st.kind] ? { [st.kind]: formationsTimeline[st.kind] } : null;
    const wins = buildDynamicWindows(teamSts, getPos, tl, effectiveTimeMin);
    if (selectedCell.windowIdx === -1) {
      videoRef.current.currentTime = 0;
    } else {
      const win = wins[selectedCell.windowIdx];
      if (win) videoRef.current.currentTime = win.startSec;
    }
  }, [selectedCell, data, pendingChanges, formationsTimeline]);

  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') return;
      if (!selectedCell || !data) return;

      const getPos = (uid) => pendingChanges[uid] ?? data.supertracks.find((s) => s.super_track_uid === uid)?.positions ?? [];
      const selSt = data.supertracks.find((s) => s.super_track_uid === selectedCell.uid);
      const teamSts = selSt ? data.supertracks.filter((s) => s.kind === selSt.kind) : [];
      const tl = selSt && formationsTimeline?.[selSt.kind]
        ? { [selSt.kind]: formationsTimeline[selSt.kind] } : null;
      const wins = buildDynamicWindows(teamSts, getPos, tl, effectiveTimeMin);

      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        const positions = getPos(selectedCell.uid);
        const ts = selectedCell.windowIdx === -1 ? 0 : wins[selectedCell.windowIdx]?.startSec ?? 0;
        const pos = getPositionAtTimestamp(positions, ts);
        if (pos) {
          setClipboard({ name: pos.name, side: pos.side });
          toast.info(`Copied: ${formatPos(pos.name, pos.side)}`, { autoClose: 1500 });
          e.preventDefault();
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && clipboard) {
        const { uid, windowIdx } = selectedCell;
        const positions = [...getPos(uid)];
        const ts = windowIdx === -1 ? 0 : wins[windowIdx]?.startSec ?? 0;
        const filtered = positions.filter((p) => (p.timestamp ?? 0) !== ts);
        filtered.push({ name: clipboard.name, side: clipboard.side, timestamp: ts, approved: true, changed: 'manual' });
        filtered.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
        setPendingChanges((prev) => ({ ...prev, [uid]: filtered }));
        toast.info(`Pasted: ${formatPos(clipboard.name, clipboard.side)}`, { autoClose: 1500 });
        e.preventDefault();
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [selectedCell, clipboard, data, pendingChanges, formationsTimeline]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: 400 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (!data) return <div className="p-3">No data</div>;

  // Player binding helpers
  const getPlayersForBind = (kind) => {
    const allPlayers = teamPlayers[kind] || [];
    const boundIds = new Set(
      data.supertracks
        .filter((st) => st.kind === kind && st.player)
        .map((st) => st.player.id)
    );
    return allPlayers.map((p) => [...p, boundIds.has(p[0])]);
  };

  const handlePlayerBind = async (playerId, playerData) => {
    if (!bindModal) return;
    const { uid } = bindModal;
    setBindingUid(uid);
    try {
      await matchApi.bindPlayer({ player: playerId, match: parseInt(matchId), super_track_uid: uid });
      setData((prev) => ({
        ...prev,
        supertracks: prev.supertracks.map((st) =>
          st.super_track_uid === uid
            ? { ...st, player: { id: playerId, first_name: playerData[1], last_name: playerData[2] } }
            : st
        ),
      }));
      setBindModal(null);
      setPlayerSearch("");
      toast.success("Player bound");
    } catch (e) {
      toast.error(`Bind failed: ${e.response?.data?.message || e.message}`);
    } finally {
      setBindingUid(null);
    }
  };

  const handlePlayerUnbind = async (uid) => {
    const st = data.supertracks.find((s) => s.super_track_uid === uid);
    if (!st?.player) return;
    try {
      await matchApi.unbindPlayer({ player: st.player.id, match: parseInt(matchId) });
      setData((prev) => ({
        ...prev,
        supertracks: prev.supertracks.map((s) =>
          s.super_track_uid === uid ? { ...s, player: null } : s
        ),
      }));
      toast.success("Player unbound");
    } catch (e) {
      toast.error(`Unbind failed: ${e.response?.data?.message || e.message}`);
    }
  };

  const handleCreateAndBind = async (name) => {
    if (!bindModal || !name.trim()) return;
    const { uid, kind } = bindModal;
    const teamId = teamIdMap[kind];
    if (!teamId) { toast.error("Team not found"); return; }

    const [firstName, ...rest] = name.trim().split(" ");
    const lastName = rest.join(" ") || "";

    setBindingUid(uid);
    try {
      const created = await matchApi.createPlayer({ first_name: firstName, last_name: lastName, team: teamId });
      const newPlayer = [created.id, created.first_name, created.last_name, null, teamId];
      setTeamPlayers((prev) => ({
        ...prev,
        [kind]: [...(prev[kind] || []), newPlayer],
      }));
      await matchApi.bindPlayer({ player: created.id, match: parseInt(matchId), super_track_uid: uid });
      setData((prev) => ({
        ...prev,
        supertracks: prev.supertracks.map((st) =>
          st.super_track_uid === uid
            ? { ...st, player: { id: created.id, first_name: created.first_name, last_name: created.last_name } }
            : st
        ),
      }));
      setBindModal(null);
      setPlayerSearch("");
      toast.success("Player created and bound");
    } catch (e) {
      toast.error(`Create failed: ${e.response?.data?.message || e.message}`);
    } finally {
      setBindingUid(null);
    }
  };

  const getEffectivePositions = (uid) => {
    const st = data.supertracks.find((s) => s.super_track_uid === uid);
    if (!st) return [];
    return pendingChanges[uid] ?? st.positions ?? [];
  };

  const teams = {};
  for (const st of data.supertracks) {
    if (!teams[st.kind]) teams[st.kind] = [];
    teams[st.kind].push(st);
  }

  const windowsByTeam = {};
  for (const [teamKind, teamSts] of Object.entries(teams)) {
    const tl = formationsTimeline?.[teamKind]
      ? { [teamKind]: formationsTimeline[teamKind] }
      : null;
    windowsByTeam[teamKind] = buildDynamicWindows(teamSts, getEffectivePositions, tl, effectiveTimeMin);
  }

  const getWindowsForUid = (uid) => {
    const st = data.supertracks.find((s) => s.super_track_uid === uid);
    return st ? (windowsByTeam[st.kind] || []) : [];
  };

  const handleCellClick = (uid, wIdx) => {
    setSelectedCell({ uid, windowIdx: wIdx });
    const st = data?.supertracks.find((s) => s.super_track_uid === uid);
    if (!st) return;
    const wins = windowsByTeam[st.kind] || [];
    if (wIdx === -1) {
      const endSec = wins.length > 0 ? wins[0].startSec : (effectiveTimeMin) * 60;
      setPitchRange({ startSec: 0, endSec });
      setPitchMinute(0);
    } else {
      const win = wins[wIdx];
      if (win) {
        setPitchRange({ startSec: win.startSec, endSec: win.endSec });
        setPitchMinute(Math.floor(win.startSec / 60));
      }
    }
  };

  const handleApprove = (uid, windowIdx) => {
    const win = getWindowsForUid(uid)[windowIdx];
    const positions = [...getEffectivePositions(uid)];

    let approvedAny = false;
    const updated = positions.map((p) => {
      if (!p.approved && (p.timestamp ?? 0) === win.startSec) {
        approvedAny = true;
        return { ...p, approved: true, changed: 'manual' };
      }
      return p;
    });

    if (!approvedAny) {
      const activePos = getPositionAtTimestamp(positions, win.startSec);
      if (activePos) {
        updated.push({
          name: activePos.name,
          side: activePos.side,
          timestamp: win.startSec,
          approved: true,
          changed: 'manual',
        });
        updated.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
      }
    }

    setPendingChanges((prev) => ({ ...prev, [uid]: updated }));
  };

  const handleApproveAll = (uid) => {
    const positions = getEffectivePositions(uid).map((p) => ({
      ...p,
      approved: true,
    }));
    setPendingChanges((prev) => ({ ...prev, [uid]: positions }));
  };

  const handleEditOpen = () => {
    if (!selectedCell) return;
    const { uid, windowIdx } = selectedCell;
    const win = getWindowsForUid(uid)[windowIdx];
    const positions = getEffectivePositions(uid);
    const activePos = getPositionAtTimestamp(positions, win.startSec);
    setEditModal({ uid, windowIdx, currentPos: activePos });
  };

  const resolvePositionCode = (uid, windowIdx, posName, posSide) => {
    const st = data?.supertracks?.find((s) => s.super_track_uid === uid);
    if (!st || !formationsCatalog) return undefined;
    const uidWindows = getWindowsForUid(uid);
    const ts = windowIdx === -1 ? 0 : uidWindows[windowIdx]?.startSec ?? 0;
    const formationName = getActiveFormation(formationsTimeline, st.kind, ts);
    if (!formationName) return undefined;
    const entry = formationsCatalog.find((f) => f.name === formationName);
    if (!entry?.slots) return undefined;
    const slot = entry.slots.find((s) => s.name === posName && s.side === posSide);
    return slot?.code;
  };

  const applyPositionChange = (uid, windowIdx, name, side, code) => {
    const positions = [...getEffectivePositions(uid)];
    const uidWindows = getWindowsForUid(uid);
    const ts = windowIdx === -1 ? 0 : uidWindows[windowIdx]?.startSec ?? 0;
    const filtered = positions.filter((p) => (p.timestamp ?? 0) !== ts);

    if (windowIdx === -1) {
      for (const w of uidWindows) {
        const hasExplicit = filtered.some((p) => (p.timestamp ?? 0) === w.startSec);
        if (hasExplicit) continue;
        const currentPos = getPositionAtTimestamp(positions, w.startSec);
        if (currentPos && currentPos.approved) {
          const c = currentPos.code || resolvePositionCode(uid, windowIdx, currentPos.name, currentPos.side);
          filtered.push({ name: currentPos.name, side: currentPos.side, timestamp: w.startSec, approved: true, changed: currentPos.changed, ...(c ? { code: c } : {}) });
        }
      }
    }

    const resolvedCode = code || resolvePositionCode(uid, windowIdx, name, side);
    filtered.push({ name, side, timestamp: ts, approved: true, changed: 'manual', ...(resolvedCode ? { code: resolvedCode } : {}) });
    filtered.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));

    const nextChanges = { [uid]: filtered };

    const editSt = data.supertracks.find((s) => s.super_track_uid === uid);
    if (editSt) {
      for (const st of data.supertracks) {
        if (st.kind !== editSt.kind || st.super_track_uid === uid) continue;
        if (nextChanges[st.super_track_uid]) continue;
        const otherPositions = [...getEffectivePositions(st.super_track_uid)];
        const otherPos = getPositionAtTimestamp(otherPositions, ts);
        if (otherPos && otherPos.name === name && otherPos.side === side) {
          const otherFiltered = otherPositions.filter((p) => (p.timestamp ?? 0) !== ts);
          if (windowIdx !== -1) {
            otherFiltered.push({ name: "", side: "", timestamp: ts, approved: true, changed: "manual" });
            otherFiltered.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
          }
          nextChanges[st.super_track_uid] = otherFiltered;
        }
      }
    }

    setPendingChanges((prev) => ({ ...prev, ...nextChanges }));
  };

  const clearPositionChange = (uid, windowIdx) => {
    const positions = [...getEffectivePositions(uid)];
    const uidWindows = getWindowsForUid(uid);
    const ts = windowIdx === -1 ? 0 : uidWindows[windowIdx]?.startSec ?? 0;
    const hasExplicit = positions.some((p) => (p.timestamp ?? 0) === ts);
    const filtered = positions.filter((p) => (p.timestamp ?? 0) !== ts);
    if (!hasExplicit && windowIdx !== -1) {
      filtered.push({ name: "", side: "", timestamp: ts, approved: true, changed: "manual" });
      filtered.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
    }
    setPendingChanges((prev) => ({ ...prev, [uid]: filtered }));
  };

  const handleEditSave = ({ name, side }) => {
    if (!editModal) return;
    applyPositionChange(editModal.uid, editModal.windowIdx, name, side);
    setEditModal(null);
  };

  const handleEditClear = () => {
    if (!editModal) return;
    clearPositionChange(editModal.uid, editModal.windowIdx);
    setEditModal(null);
  };

  const handleDeleteColumn = (teamKind, windowIdx) => {
    const teamSts = data.supertracks.filter((s) => s.kind === teamKind);
    const tl = formationsTimeline?.[teamKind] ? { [teamKind]: formationsTimeline[teamKind] } : null;
    const wins = buildDynamicWindows(teamSts, getEffectivePositions, tl, effectiveTimeMin);
    const win = wins[windowIdx];
    if (!win) return;

    const isLast = windowIdx >= wins.length - 1;
    const nextStart = wins[windowIdx + 1]?.startSec;

    setPendingChanges((prev) => {
      const next = { ...prev };
      for (const st of teamSts) {
        const positions = [...getEffectivePositions(st.super_track_uid)];
        const filtered = positions.filter((p) => {
          const ts = p.timestamp ?? 0;
          if (ts === 0) return true;
          if (ts < win.startSec) return true;
          if (isLast) return false;
          return ts >= nextStart;
        });
        if (!isLast && nextStart != null && !filtered.some((p) => (p.timestamp ?? 0) === nextStart)) {
          let latest = null;
          for (const p of positions) {
            const ts = p.timestamp ?? 0;
            if (ts >= win.startSec && ts < nextStart && p.name) latest = p;
          }
          if (latest) {
            const fallback = getPositionAtTimestamp(filtered, nextStart);
            if (!fallback || fallback.name !== latest.name || fallback.side !== latest.side) {
              filtered.push({ ...latest, timestamp: nextStart, changed: "manual" });
              filtered.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
            }
          }
        }
        if (filtered.length !== positions.length || filtered.some((f, idx) => f !== positions[idx])) {
          next[st.super_track_uid] = filtered;
        }
      }
      return next;
    });

    setFormationsTimeline((prev) => {
      const runs = [...(prev[teamKind] || [])];
      const rangeEnd = isLast ? Infinity : nextStart;
      for (let i = runs.length - 1; i >= 0; i--) {
        if (runs[i].start_sec < win.startSec || runs[i].start_sec >= rangeEnd) continue;
        if (i === 0) {
          if (runs.length <= 1) continue;
          if (runs[i].end_sec > rangeEnd) {
            runs[i] = { ...runs[i], start_sec: rangeEnd };
          } else {
            runs[1] = { ...runs[1], start_sec: runs[0].start_sec };
            runs.splice(0, 1);
          }
        } else {
          if (runs[i].end_sec > rangeEnd) {
            runs[i] = { ...runs[i], start_sec: rangeEnd };
            runs[i - 1] = { ...runs[i - 1], end_sec: rangeEnd };
          } else {
            runs[i - 1] = { ...runs[i - 1], end_sec: runs[i].end_sec };
            runs.splice(i, 1);
          }
        }
      }
      for (let i = runs.length - 1; i > 0; i--) {
        if (runs[i].formation === runs[i - 1].formation) {
          runs[i - 1] = { ...runs[i - 1], end_sec: runs[i].end_sec };
          runs.splice(i, 1);
        }
      }
      return { ...prev, [teamKind]: runs };
    });
  };

  const handleDeleteFormation = (teamKind, runIndex) => {
    setFormationsTimeline((prev) => {
      const runs = [...(prev[teamKind] || [])];
      if (runIndex <= 0 || runIndex >= runs.length) return prev;
      runs[runIndex - 1] = { ...runs[runIndex - 1], end_sec: runs[runIndex].end_sec };
      runs.splice(runIndex, 1);
      return { ...prev, [teamKind]: runs };
    });
  };

  const handleAddFormation = (teamKind, minuteVal, formation) => {
    const sec = minuteVal * 60;
    setFormationsTimeline((prev) => {
      const runs = [...(prev[teamKind] || [])];
      const originalLastEnd = runs.length > 0 ? runs[runs.length - 1].end_sec : 0;
      let insertIdx = runs.length;
      for (let i = 0; i < runs.length; i++) {
        if (runs[i].start_sec > sec) { insertIdx = i; break; }
      }
      if (insertIdx > 0) {
        runs[insertIdx - 1] = { ...runs[insertIdx - 1], end_sec: sec };
      }
      const matchTimeSec = (effectiveTimeMin) * 60;
      const endSec = insertIdx < runs.length ? runs[insertIdx].start_sec : Math.max(matchTimeSec, originalLastEnd);
      runs.splice(insertIdx, 0, { start_sec: sec, end_sec: endSec, formation });
      return { ...prev, [teamKind]: runs };
    });
    setAddFormationModal(null);
  };

  const handleRestart = async () => {
    setRestarting(true);
    try {
      // Save pending position changes first (e.g. cleared start positions after formation change)
      const pendingUids = Object.keys(pendingChanges);
      if (pendingUids.length > 0) {
        const updates = pendingUids.map((uid) => ({
          super_track_uid: parseInt(uid),
          positions: pendingChanges[uid],
        }));
        await matchApi.updateVerifyPositions(matchId, updates);
        setPendingChanges({});
      }

      // Always save formations before restart
      await matchApi.updateFormations(matchId, {
        left_team: formationLeft,
        right_team: formationRight,
        formations_timeline: formationsTimeline,
      });
      await matchApi.restartVerifyPositions(matchId, {
        role_detection_method: roleMethod,
      });
      toast.info(`ML started (${roleMethod}) — polling for results...`);

      // Poll until positions change or timeout
      const startPositions = JSON.stringify(
        data.supertracks.map((s) => ({ uid: s.super_track_uid, p: s.positions }))
      );
      const maxAttempts = 60; // 5 minutes max
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const fresh = await matchApi.getVerifyPositions(matchId);
          const freshPositions = JSON.stringify(
            fresh.supertracks.map((s) => ({ uid: s.super_track_uid, p: s.positions }))
          );
          if (freshPositions !== startPositions) {
            setData(fresh);
            setPendingChanges({});
            setFormationLeft(fresh.formations?.left_team || "");
            setFormationRight(fresh.formations?.right_team || "");
            toast.success("Positions updated");
            return;
          }
        } catch {
          // ignore polling errors
        }
      }
      toast.warning("Timeout — refresh manually");
    } catch (e) {
      toast.error("Restart failed");
      console.error(e);
    } finally {
      setRestarting(false);
    }
  };

  const formationChanged =
    formationLeft !== (data?.formations?.left_team || "") ||
    formationRight !== (data?.formations?.right_team || "");

  const timelineChanged =
    JSON.stringify(formationsTimeline) !== JSON.stringify(data?.formations_timeline || {});

  const handleSave = async () => {
    const uids = Object.keys(pendingChanges);
    if (uids.length === 0 && !formationChanged && !timelineChanged) return toast.info("No changes to save");
    setSaving(true);
    try {
      if (formationChanged || timelineChanged) {
        await matchApi.updateFormations(matchId, {
          left_team: formationLeft,
          right_team: formationRight,
          formations_timeline: formationsTimeline,
        });
      }
      let savedCount = 0;
      if (uids.length > 0) {
        const updates = uids.map((uid) => ({
          super_track_uid: parseInt(uid),
          positions: pendingChanges[uid],
        }));
        await matchApi.updateVerifyPositions(matchId, updates);
        savedCount = updates.length;
      }
      const anyFormationSaved = formationChanged || timelineChanged;
      toast.success(
        anyFormationSaved && savedCount > 0
          ? `Saved formation + ${savedCount} supertrack(s)`
          : anyFormationSaved
          ? "Formation saved"
          : `Saved ${savedCount} supertrack(s)`
      );
      await fetchData();
    } catch (e) {
      toast.error("Save failed");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  // Video tracks for the selected supertrack
  const { tracks: videoTracks, colors: videoColors } = selectedCell
    ? buildVideoTracks(allTracks, superTracksMap, selectedCell.uid)
    : { tracks: {}, colors: {} };

  const selectedSt = selectedCell
    ? data.supertracks.find((s) => s.super_track_uid === selectedCell.uid)
    : null;
  const selectedUidWindows = selectedCell ? getWindowsForUid(selectedCell.uid) : [];
  const selectedWin = selectedCell ? selectedUidWindows[selectedCell.windowIdx] : null;
  const selectedPositions = selectedCell ? getEffectivePositions(selectedCell.uid) : [];
  const selectedActivePos = selectedWin
    ? getPositionAtTimestamp(selectedPositions, selectedWin.startSec)
    : null;

  // Formation pitch selector data
  const fpAllTeamSupertracks = selectedSt
    ? data.supertracks.filter((s) => s.kind === selectedSt.kind)
    : [];
  const fpFormationName = selectedSt
    ? selectedCell.windowIdx === -1
      ? (selectedSt.kind === "left_team" ? formationLeft : formationRight)
      : getActiveFormation(formationsTimeline, selectedSt.kind, selectedWin?.startSec ?? 0)
    : null;

  const fpStartSec = selectedCell
    ? (selectedCell.windowIdx === -1 ? 0 : (selectedWin?.startSec ?? 0))
    : 0;
  const fpEndSec = selectedCell
    ? (selectedCell.windowIdx === -1
      ? ((windowsByTeam[selectedSt?.kind]?.[0]?.startSec) || ((effectiveTimeMin) * 60))
      : (selectedWin?.endSec ?? (effectiveTimeMin) * 60))
    : 0;

  const fpTeamSupertracksRaw = fpAllTeamSupertracks.filter((st) => {
    if (selectedCell?.windowIdx === -1) return st.start_lineup === true;
    if (hasWeightedPosition(data.weighted_positions, st.kind, st.super_track_uid, fpStartSec, fpEndSec)) return true;
    const isLastWindow = selectedCell?.windowIdx === (windowsByTeam[st.kind] || []).length - 1;
    if (isLastWindow && st.start_lineup === false) {
      const positions = getEffectivePositions(st.super_track_uid);
      const pos = getPositionAtTimestamp(positions, fpStartSec, fpEndSec);
      if (pos && pos.name) return true;
    }
    return false;
  });
  const ppTeam = data.players_per_team || 11;
  let fpTeamSupertracks = fpTeamSupertracksRaw;
  if (fpTeamSupertracksRaw.length > ppTeam && selectedCell?.windowIdx !== -1) {
    const teamWins = windowsByTeam[selectedSt?.kind] || [];
    let prevStart, prevEnd;
    if (selectedCell.windowIdx === 0) {
      prevStart = 0;
      prevEnd = teamWins[0]?.startSec ?? 0;
    } else {
      const pw = teamWins[selectedCell.windowIdx - 1];
      prevStart = pw?.startSec ?? 0;
      prevEnd = pw?.endSec ?? 0;
    }
    const onField = fpTeamSupertracksRaw.filter((st) =>
      hasWeightedPosition(data.weighted_positions, st.kind, st.super_track_uid, prevStart, prevEnd)
    );
    const notOnField = fpTeamSupertracksRaw.filter((st) =>
      !hasWeightedPosition(data.weighted_positions, st.kind, st.super_track_uid, prevStart, prevEnd)
    );
    const slots = Math.max(0, ppTeam - onField.length);
    fpTeamSupertracks = [...onField, ...notOnField.slice(0, slots)];
    const isLastWindow = selectedCell?.windowIdx === (teamWins.length - 1);
    if (isLastWindow) {
      for (const st of fpTeamSupertracksRaw) {
        if (!fpTeamSupertracks.some((s) => s.super_track_uid === st.super_track_uid)) {
          fpTeamSupertracks.push(st);
        }
      }
    }
  }

  const fpPositionsByUid = {};
  if (selectedCell) {
    for (const st of fpTeamSupertracks) {
      const positions = getEffectivePositions(st.super_track_uid);
      fpPositionsByUid[st.super_track_uid] = getPositionAtTimestamp(positions, fpStartSec);
    }
  }

  const hasPending = Object.keys(pendingChanges).length > 0;
  const videoSrc = match?.video?.origin;
  const playersPerTeam = data.players_per_team || 11;

  const validateFormation = (teamKind, formation) => {
    const teamSts = data.supertracks.filter(
      (s) => s.kind === teamKind && s.start_lineup === true
    );
    const counts = { gk: 0, def: 0, dmid: 0, mid: 0, amf: 0, fwd: 0, unset: 0 };
    let total = 0;
    for (const st of teamSts) {
      const positions = getEffectivePositions(st.super_track_uid);
      const startPos = positions.find((p) => (p.timestamp ?? 0) === 0);
      if (!startPos) { counts.unset++; continue; }
      const line = POSITION_LINE[startPos.name];
      if (line) counts[line]++;
      total++;
    }
    const expCounts = getLineCounts(formation, formationsCatalog);
    if (!expCounts) {
      return { actual: counts, expected: null, total, ok: total > 0, empty: total === 0 };
    }
    if (total === 0) {
      return { actual: counts, expected: expCounts, total, ok: true, empty: true };
    }
    const ok =
      counts.gk === expCounts.gk &&
      counts.def === expCounts.def &&
      counts.dmid === expCounts.dmid &&
      counts.mid === expCounts.mid &&
      counts.amf === expCounts.amf &&
      counts.fwd === expCounts.fwd;
    return { actual: counts, expected: expCounts, total, ok, empty: false };
  };

  const renderFormationStatus = (teamKind, formation) => {
    if (!formation) return null;
    const v = validateFormation(teamKind, formation);
    const fmt = (c) =>
      `${c.gk}GK ${c.def}D${c.dmid ? ` ${c.dmid}DM` : ""} ${c.mid}M${c.amf ? ` ${c.amf}AM` : ""} ${c.fwd}F`;
    return (
      <span style={{ fontSize: 11, marginLeft: 4 }} title={`Got: ${fmt(v.actual)}`}>
        {v.empty ? (
          <Badge bg="secondary" pill>○ unset (ML will fill)</Badge>
        ) : v.ok ? (
          <Badge bg="success" pill>✓ {fmt(v.actual)}</Badge>
        ) : v.expected ? (
          <Badge bg="danger" pill title={`Expected: ${fmt(v.expected)}`}>
            ✗ {fmt(v.actual)} ≠ {fmt(v.expected)}
          </Badge>
        ) : null}
      </span>
    );
  };

  // Block Save/Restart if any team has formation set but start positions don't match
  const leftValid = !formationLeft || validateFormation("left_team", formationLeft).ok;
  const rightValid = !formationRight || validateFormation("right_team", formationRight).ok;
  const formationsValid = leftValid && rightValid;

  return (
    <div className="verify-positions p-3">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={["fas", "arrow-left"]} /> Back
          </Button>
          <h5 className="mb-0">Verify Positions — Match {matchId}</h5>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Form.Select
            size="sm"
            style={{ width: "auto" }}
            value={roleMethod}
            onChange={(e) => setRoleMethod(e.target.value)}
          >
            <option value="hungarian">Hungarian</option>
            <option value="hybrid">Hybrid</option>
            <option value="angular_profile">Angular Profile</option>
          </Form.Select>
          <Button
            variant="warning"
            size="sm"
            onClick={handleRestart}
            disabled={restarting}
            title={!formationsValid ? "⚠ Start positions don't match formation" : ""}
          >
            {restarting ? <Spinner size="sm" animation="border" /> : <FontAwesomeIcon icon={["fas", "rotate"]} />}
            {" "}Restart ML
          </Button>
          <Button
            variant={formationsValid ? "success" : "outline-danger"}
            size="sm"
            onClick={handleSave}
            disabled={(!hasPending && !formationChanged && !timelineChanged) || saving}
            title={!formationsValid ? "⚠ Start positions don't match formation" : ""}
          >
            {saving ? <Spinner size="sm" animation="border" /> : <FontAwesomeIcon icon={["fas", "save"]} />}
            {" "}Save {hasPending && <Badge bg="warning">{Object.keys(pendingChanges).length}</Badge>}
            {(formationChanged || timelineChanged) && <Badge bg="info" className="ms-1">F</Badge>}
          </Button>
        </div>
      </div>

      {/* Formation selectors & pitch mode */}
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap" style={{ fontSize: 13 }}>
        <div className="d-flex align-items-center gap-1">
          <span className="text-muted">Left:</span>
          <Form.Select
            size="sm"
            value={formationLeft}
            onChange={(e) => {
              const val = e.target.value;
              setFormationLeft(val);
              resetStartPositions("left_team");
              setFormationsTimeline((prev) => {
                const runs = prev.left_team;
                if (runs && runs.length > 0) {
                  const updated = [...runs];
                  updated[0] = { ...updated[0], formation: val };
                  return { ...prev, left_team: updated };
                }
                return prev;
              });
            }}
            style={{ width: 130 }}
          >
            <option value="">Not set</option>
            {(formationsCatalog || []).map((f) => (
              <option key={f.name} value={f.name}>{f.display_name || f.name}</option>
            ))}
          </Form.Select>
          {renderFormationStatus("left_team", formationLeft)}
        </div>
        <div className="d-flex align-items-center gap-1">
          <span className="text-muted">Right:</span>
          <Form.Select
            size="sm"
            value={formationRight}
            onChange={(e) => {
              const val = e.target.value;
              setFormationRight(val);
              resetStartPositions("right_team");
              setFormationsTimeline((prev) => {
                const runs = prev.right_team;
                if (runs && runs.length > 0) {
                  const updated = [...runs];
                  updated[0] = { ...updated[0], formation: val };
                  return { ...prev, right_team: updated };
                }
                return prev;
              });
            }}
            style={{ width: 130 }}
          >
            <option value="">Not set</option>
            {(formationsCatalog || []).map((f) => (
              <option key={f.name} value={f.name}>{f.display_name || f.name}</option>
            ))}
          </Form.Select>
          {renderFormationStatus("right_team", formationRight)}
        </div>
        <div className="d-flex align-items-center gap-1 ms-auto">
          <span className="text-muted">Pitch:</span>
          <div className="btn-group btn-group-sm">
            <Button
              variant={pitchMode === "window" ? "primary" : "outline-primary"}
              onClick={() => setPitchMode("window")}
            >
              Window
            </Button>
            <Button
              variant={pitchMode === "match" ? "primary" : "outline-primary"}
              onClick={() => setPitchMode("match")}
            >
              Full match
            </Button>
          </div>
        </div>
      </div>

      {/* Formation timeline bars */}
      {(formationsTimeline?.left_team || formationsTimeline?.right_team) && (
        <div className="mb-3 d-flex flex-column gap-1">
          <FormationTimeline
            timeline={formationsTimeline.left_team}
            matchTimeSec={(effectiveTimeMin) * 60}
            label="Left"
          />
          <FormationTimeline
            timeline={formationsTimeline.right_team}
            matchTimeSec={(effectiveTimeMin) * 60}
            label="Right"
          />
        </div>
      )}

      <div className="verify-layout">
        {/* Left: table */}
        <div className="verify-table-container">
          <div className="legend mb-2 d-flex gap-3" style={{ fontSize: 12 }}>
            <span><span className="legend-dot legend-approved" /> Approved</span>
            <span><span className="legend-dot legend-auto" /> ML-detected</span>
            <span><span className="legend-dot legend-changed" /> Changed in window</span>
            <span><span className="legend-dot legend-pending" /> Pending save</span>
          </div>

          {Object.entries(teams).map(([teamKind, teamSts]) => {
            const teamWindows = windowsByTeam[teamKind] || [];

            // Pre-compute allowed combos and column validity per window
            const windowFormations = teamWindows.map((w) => getActiveFormation(formationsTimeline, teamKind, w.startSec));
            const windowAllowed = windowFormations.map((f) => f ? getAllowedCombos(f, formationsCatalog) : null);
            const windowHasUnapproved = teamWindows.map((w, wIdx) => {
              return teamSts.some((st) => {
                const positions = getEffectivePositions(st.super_track_uid);
                const pos = getPositionAtTimestamp(positions, w.startSec, w.endSec);
                return pos && !pos.approved;
              });
            });
            const windowOnField = teamWindows.map((w, wIdx) => {
              const withWP = teamSts.filter((st) =>
                hasWeightedPosition(data.weighted_positions, st.kind, st.super_track_uid, w.startSec, w.endSec)
              );
              let result;
              if (withWP.length <= playersPerTeam) {
                result = new Set(withWP.map((st) => st.super_track_uid));
              } else {
                let ps, pe;
                if (wIdx === 0) {
                  ps = 0;
                  pe = w.startSec;
                } else {
                  ps = teamWindows[wIdx - 1].startSec;
                  pe = teamWindows[wIdx - 1].endSec;
                }
                const prev = withWP.filter((st) =>
                  hasWeightedPosition(data.weighted_positions, st.kind, st.super_track_uid, ps, pe)
                );
                const fresh = withWP.filter((st) =>
                  !hasWeightedPosition(data.weighted_positions, st.kind, st.super_track_uid, ps, pe)
                );
                const slots = Math.max(0, playersPerTeam - prev.length);
                result = new Set([...prev, ...fresh.slice(0, slots)].map((st) => st.super_track_uid));
              }
              if (wIdx === teamWindows.length - 1) {
                for (const st of teamSts) {
                  if (st.start_lineup === false && !result.has(st.super_track_uid)) {
                    const positions = getEffectivePositions(st.super_track_uid);
                    const pos = getPositionAtTimestamp(positions, w.startSec, w.endSec);
                    if (pos && pos.name) result.add(st.super_track_uid);
                  }
                }
              }
              return result;
            });
            const windowHasMismatch = teamWindows.map((w, wIdx) => {
              const allowed = windowAllowed[wIdx];
              if (!allowed) return false;
              return teamSts.some((st) => {
                if (!windowOnField[wIdx].has(st.super_track_uid)) return false;
                const positions = getEffectivePositions(st.super_track_uid);
                const pos = getPositionAtTimestamp(positions, w.startSec, w.endSec);
                if (!pos) return false;
                return !allowed.has(`${pos.name}|${pos.side}`);
              });
            });

            const handleApproveColumn = (wIdx) => {
              const w = teamWindows[wIdx];
              setPendingChanges((prev) => {
                const next = { ...prev };
                for (const st of teamSts) {
                  const uid = st.super_track_uid;
                  const positions = [...getEffectivePositions(uid)];
                  const pos = getPositionAtTimestamp(positions, w.startSec, w.endSec);
                  if (!pos || pos.approved) continue;
                  const posTs = pos.timestamp ?? 0;
                  const updated = positions.map((p) =>
                    (p.timestamp ?? 0) === posTs ? { ...p, approved: true, changed: 'manual' } : p
                  );
                  if (!positions.some((p) => (p.timestamp ?? 0) === posTs)) {
                    updated.push({ name: pos.name, side: pos.side, timestamp: posTs, approved: true, changed: 'manual' });
                    updated.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
                  }
                  next[uid] = updated;
                }
                return next;
              });
            };

            return (
            <div key={teamKind} className="mb-3">
              <h6 className="text-muted mb-1 d-flex align-items-center gap-2">
                {teamKind}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  style={{ fontSize: 10, padding: "0px 5px" }}
                  onClick={() => setAddFormationModal({ teamKind, defaultMinute: pitchMinute || 0 })}
                >
                  + Formation
                </Button>
              </h6>
              <div className="table-responsive">
                <table className="table table-sm table-bordered verify-table">
                  <thead>
                    {/* Formation timeline header row */}
                    {formationsTimeline?.[teamKind]?.length > 0 && (
                      <tr className="formation-header-row">
                        <th className="sticky-col id-col" />
                        <th className="sticky-col jersey-col" />
                        <th className="sticky-col sl-col" />
                        <th className="sticky-col name-col" />
                        <th />
                        {teamWindows.map((w, wi) => {
                          const runs = formationsTimeline[teamKind];
                          const run = runs.find((r) => w.startSec >= r.start_sec && w.startSec < r.end_sec);
                          if (!run) return <th key={wi} />;

                          const runAllowed = getAllowedCombos(run.formation, formationsCatalog);
                          const fLines = getLineCounts(run.formation, formationsCatalog);
                          let winMismatch = false;
                          const lineCounts = { gk: 0, def: 0, dmid: 0, mid: 0, amf: 0, fwd: 0 };
                          if (runAllowed) {
                            for (const st of teamSts) {
                              if (!windowOnField[wi]?.has(st.super_track_uid)) continue;
                              const positions = getEffectivePositions(st.super_track_uid);
                              const pos = getPositionAtTimestamp(positions, w.startSec, w.endSec);
                              if (!pos) continue;
                              const line = POSITION_LINE[pos.name];
                              if (line) lineCounts[line]++;
                              if (!runAllowed.has(`${pos.name}|${pos.side}`)) winMismatch = true;
                            }
                          }
                          if (fLines && !winMismatch) {
                            for (const key of ["def", "dmid", "mid", "amf", "fwd"]) {
                              if ((fLines[key] || 0) !== (lineCounts[key] || 0)) { winMismatch = true; break; }
                            }
                            if (lineCounts.gk !== (fLines.gk || 0)) winMismatch = true;
                          }

                          const prevWinFormation = wi > 0
                            ? (runs.find((r) => teamWindows[wi - 1].startSec >= r.start_sec && teamWindows[wi - 1].startSec < r.end_sec) || {}).formation
                            : null;
                          const diffFromPrev = prevWinFormation != null && prevWinFormation !== run.formation;

                          const borderColor = !runAllowed ? "#d1d5db" : winMismatch ? "#ef4444" : diffFromPrev ? "#f59e0b" : "#22c55e";

                          const lineLabels = { def: "D", dmid: "DM", mid: "M", amf: "AM", fwd: "F" };
                          let metaParts = [];
                          if (fLines) {
                            for (const [key, label] of Object.entries(lineLabels)) {
                              if (fLines[key]) metaParts.push(`${label}: ${lineCounts[key] || 0}/${fLines[key]}`);
                            }
                          }

                          return (
                            <th key={wi} className="text-center formation-cell" style={{ padding: "2px 4px" }}>
                              <div style={{ border: `2px solid ${borderColor}`, borderRadius: 4, padding: "2px 4px", display: "inline-block" }}>
                                <div className="d-flex align-items-center justify-content-center gap-1">
                                  <Form.Select
                                    size="sm"
                                    className="formation-select"
                                    value={run.formation}
                                    onChange={(e) => {
                                      const newF = e.target.value;
                                      setFormationsTimeline((prev) => {
                                        const allRuns = [...(prev[teamKind] || [])];
                                        const ri = allRuns.findIndex((r) => w.startSec >= r.start_sec && w.startSec < r.end_sec);
                                        if (ri === -1) return prev;
                                        const r = allRuns[ri];

                                        if (r.start_sec === w.startSec && w.endSec >= r.end_sec) {
                                          allRuns[ri] = { ...r, formation: newF };
                                        } else if (r.start_sec === w.startSec) {
                                          allRuns[ri] = { ...r, start_sec: w.endSec };
                                          allRuns.splice(ri, 0, { start_sec: w.startSec, end_sec: w.endSec, formation: newF });
                                        } else if (w.endSec >= r.end_sec) {
                                          allRuns[ri] = { ...r, end_sec: w.startSec };
                                          allRuns.splice(ri + 1, 0, { start_sec: w.startSec, end_sec: r.end_sec, formation: newF });
                                        } else {
                                          allRuns[ri] = { ...r, end_sec: w.startSec };
                                          allRuns.splice(ri + 1, 0,
                                            { start_sec: w.startSec, end_sec: w.endSec, formation: newF },
                                            { start_sec: w.endSec, end_sec: r.end_sec, formation: r.formation },
                                          );
                                        }

                                        // Merge adjacent runs with same formation
                                        for (let i = allRuns.length - 1; i > 0; i--) {
                                          if (allRuns[i].formation === allRuns[i - 1].formation) {
                                            allRuns[i - 1] = { ...allRuns[i - 1], end_sec: allRuns[i].end_sec };
                                            allRuns.splice(i, 1);
                                          }
                                        }

                                        return { ...prev, [teamKind]: allRuns };
                                      });
                                    }}
                                  >
                                    {(formationsCatalog || []).map((f) => (
                                      <option key={f.name} value={f.name}>{f.name}</option>
                                    ))}
                                    {(!formationsCatalog || !formationsCatalog.some((f) => f.name === run.formation)) && (
                                      <option value={run.formation}>{run.formation}</option>
                                    )}
                                  </Form.Select>
                                  <span
                                    className="text-danger"
                                    style={{ cursor: "pointer", fontWeight: "bold", fontSize: 14, lineHeight: 1 }}
                                    title="Delete this column (remove all position changes at this time)"
                                    onClick={() => handleDeleteColumn(teamKind, wi)}
                                  >
                                    &times;
                                  </span>
                                </div>
                                {metaParts.length > 0 && (
                                  <div style={{ fontSize: 8, color: winMismatch ? "#dc2626" : "#6b7280", fontWeight: 600, marginTop: 1 }}>
                                    {metaParts.join(" ")}
                                  </div>
                                )}
                              </div>
                            </th>
                          );
                        })}
                        <th />
                      </tr>
                    )}
                    <tr>
                      <th className="sticky-col id-col">ID</th>
                      <th className="sticky-col jersey-col">Jersey</th>
                      <th className="sticky-col sl-col" title="Start Lineup">SL</th>
                      <th className="sticky-col name-col">Name</th>
                      <th className="text-center start-col">Start</th>
                      {teamWindows.map((w, i) => (
                        <th key={i} className={`text-center window-header${windowHasMismatch[i] ? " window-mismatch" : ""}`}>
                          {w.label}
                          {windowHasUnapproved[i] && (
                            <FontAwesomeIcon
                              icon={["fas", "check-double"]}
                              className="ms-1 approve-icon"
                              title="Approve entire column"
                              style={{ cursor: "pointer" }}
                              onClick={() => handleApproveColumn(i)}
                            />
                          )}
                        </th>
                      ))}
                      <th className="text-center">Act</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const playerIdCounts = {};
                      for (const s of teamSts) {
                        if (s.player?.id) playerIdCounts[s.player.id] = (playerIdCounts[s.player.id] || 0) + 1;
                      }
                      return teamSts.map((st) => {
                      const uid = st.super_track_uid;
                      const positions = getEffectivePositions(uid);
                      const isPending = uid in pendingChanges;
                      const hasAnyAuto = positions.some((p) => !p.approved);
                      const startPos = positions.find((p) => (p.timestamp ?? 0) === 0) || null;
                      const isDuplicatePlayer = st.player?.id && playerIdCounts[st.player.id] > 1;

                      return (
                        <tr key={uid} className={isPending ? "row-pending" : ""}>
                          <td className="sticky-col id-col">{uid}</td>
                          <td className="sticky-col jersey-col"><strong>{st.jersey || "?"}</strong></td>
                          <td className="sticky-col sl-col text-center">
                            {st.start_lineup ? "✓" : ""}
                          </td>
                          <td
                            className="sticky-col name-col"
                            style={{ cursor: "pointer", ...(isDuplicatePlayer ? { outline: "2px solid #f59e0b", outlineOffset: "-2px" } : {}) }}
                            title={st.player ? (isDuplicatePlayer ? "⚠ Duplicate player in team! Click to change" : "Click to change / right-click to unbind") : "Click to bind player"}
                            onClick={() => {
                              setBindModal({ uid, kind: st.kind });
                              setPlayerSearch("");
                            }}
                            onContextMenu={(e) => {
                              if (!st.player) return;
                              e.preventDefault();
                              handlePlayerUnbind(uid);
                            }}
                          >
                            {st.player ? (
                              <span className="d-flex align-items-center gap-1">
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {`${st.player.last_name || ""} ${st.player.first_name || ""}`.trim()}
                                </span>
                                <span
                                  className="text-danger"
                                  style={{ cursor: "pointer", fontWeight: "bold", fontSize: 18, lineHeight: 1, flexShrink: 0, padding: "0 2px" }}
                                  title="Unbind player"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePlayerUnbind(uid);
                                  }}
                                >
                                  &times;
                                </span>
                              </span>
                            ) : (
                              <span className="text-muted" style={{ fontStyle: "italic", fontSize: 11 }}>bind...</span>
                            )}
                          </td>
                          <td
                            className={`text-center start-col pos-cell${
                              selectedCell?.uid === uid && selectedCell?.windowIdx === -1 ? " cell-selected" : ""
                            }`}
                            title={
                              startPos
                                ? `${startPos.name} (${startPos.side}) [${startPos.changed || 'base'}]`
                                : "Double-click to set"
                            }
                            style={{ cursor: "pointer" }}
                            onClick={() => handleCellClick(uid, -1)}
                            onDoubleClick={() => {
                              handleCellClick(uid, -1);
                              setEditModal({ uid, windowIdx: -1, currentPos: startPos });
                            }}
                          >
                            <span className="pos-label">
                              {startPos ? formatPos(startPos.name, startPos.side) : "—"}
                            </span>
                            {startPos && !startPos.approved && (
                              <FontAwesomeIcon
                                icon={["fas", "check"]}
                                className="ms-1 approve-icon"
                                title="Approve start position"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const positions = [...getEffectivePositions(uid)];
                                  const updated = positions.map((p) =>
                                    (p.timestamp ?? 0) === 0 ? { ...p, approved: true } : p
                                  );
                                  setPendingChanges((prev) => ({ ...prev, [uid]: updated }));
                                }}
                              />
                            )}
                          </td>
                          {teamWindows.map((w, wIdx) => {
                            const hasWP = hasWeightedPosition(
                              data.weighted_positions, st.kind, uid, w.startSec, w.endSec
                            );
                            const isLastWindow = wIdx === teamWindows.length - 1;
                            const hasPendingPos = getPositionAtTimestamp(positions, w.startSec, w.endSec);
                            const lateSubAllowed = isLastWindow && st.start_lineup === false;

                            if (!hasWP && !(lateSubAllowed && hasPendingPos)) {
                              if (st.start_lineup === false && !isLastWindow) {
                                return (
                                  <td key={wIdx} className="pos-cell text-muted"
                                    style={{ cursor: "not-allowed", opacity: 0.5 }}
                                    title="Substitute not on field in this window"
                                  >
                                    NA
                                  </td>
                                );
                              }
                              return (
                                <td key={wIdx} className="pos-cell text-muted"
                                  style={{ cursor: "default" }}
                                  onClick={() => handleCellClick(uid, wIdx)}
                                >
                                  —
                                </td>
                              );
                            }

                            if (!windowOnField[wIdx]?.has(uid) && !(lateSubAllowed && hasPendingPos)) {
                              if (lateSubAllowed) {
                                return (
                                  <td key={wIdx} className="pos-cell text-muted"
                                    style={{ cursor: "default" }}
                                    onClick={() => handleCellClick(uid, wIdx)}
                                  >
                                    —
                                  </td>
                                );
                              }
                              return (
                                <td key={wIdx} className="pos-cell text-muted"
                                  style={{ cursor: "not-allowed", opacity: 0.5 }}
                                  title="Displaced by substitution in this window"
                                >
                                  NA
                                </td>
                              );
                            }

                            const activePos = getPositionAtTimestamp(positions, w.startSec, w.endSec);
                            const label = activePos ? formatPos(activePos.name, activePos.side) : "";
                            const isAuto = activePos ? !activePos.approved : false;
                            const winChangeEntry = positions.find(
                              (p) => !p.approved && (p.timestamp ?? 0) >= w.startSec && (p.timestamp ?? 0) < w.endSec
                            );
                            const needsApproval = !!winChangeEntry;
                            const isSelected =
                              selectedCell?.uid === uid && selectedCell?.windowIdx === wIdx;

                            const allowed = windowAllowed[wIdx];
                            const isMismatch = allowed && activePos && !allowed.has(`${activePos.name}|${activePos.side}`);

                            let cellClass = "pos-cell";
                            if (isMismatch) cellClass += " cell-mismatch";
                            if (isSelected) cellClass += " cell-selected";
                            else if (needsApproval) cellClass += " cell-changed";
                            else if (isAuto) cellClass += " cell-auto";

                            const changedType = activePos?.changed;

                            return (
                              <td
                                key={wIdx}
                                className={cellClass}
                                title={activePos ? `${activePos.name} (${activePos.side}) [${changedType || 'base'}]` : ""}
                                onClick={() => handleCellClick(uid, wIdx)}
                                onDoubleClick={() => {
                                  handleCellClick(uid, wIdx);
                                  setEditModal({ uid, windowIdx: wIdx, currentPos: activePos });
                                }}
                              >
                                <span className="pos-label">{label}</span>
                                {needsApproval && (
                                  <FontAwesomeIcon
                                    icon={["fas", "check"]}
                                    className="ms-1 approve-icon"
                                    title="Approve"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleApprove(uid, wIdx);
                                    }}
                                  />
                                )}
                              </td>
                            );
                          })}
                          <td className="text-center">
                            <div className="d-flex gap-1 justify-content-center">
                              {hasAnyAuto && (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  title="Approve all"
                                  onClick={() => handleApproveAll(uid)}
                                  style={{ fontSize: 11, padding: "1px 6px" }}
                                >
                                  <FontAwesomeIcon icon={["fas", "check-double"]} />
                                </Button>
                              )}
                              {startPos && (
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  title="Apply start position to all — clear all changes"
                                  onClick={() => {
                                    const ts0 = positions.find((p) => (p.timestamp ?? 0) === 0);
                                    if (!ts0) return;
                                    setPendingChanges((prev) => ({ ...prev, [uid]: [{ ...ts0, approved: true }] }));
                                  }}
                                  style={{ fontSize: 11, padding: "1px 6px" }}
                                >
                                  <FontAwesomeIcon icon={["fas", "arrows-left-right-to-line"]} />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    });})()}
                  </tbody>
                </table>
              </div>
            </div>
          );
          })}
        </div>

        {/* Right: video preview */}
        <div
          ref={videoContainerRef}
          className={`verify-video-container ${isFullscreen ? "verify-video-fullscreen" : ""}`}
        >
          {/* View mode toggle */}
          <div className="mb-2 d-flex align-items-center gap-2">
            <div className="btn-group btn-group-sm">
              <Button
                variant={playerView === "formation" ? "primary" : "outline-primary"}
                onClick={() => setPlayerView("formation")}
                style={{ fontSize: 11, padding: "2px 8px" }}
              >
                Formation
              </Button>
              <Button
                variant={playerView === "video" ? "primary" : "outline-primary"}
                onClick={() => setPlayerView("video")}
                style={{ fontSize: 11, padding: "2px 8px" }}
              >
                Video
              </Button>
            </div>
            {selectedCell && (
              <>
                <Badge bg="secondary">
                  {selectedSt?.jersey} {selectedSt?.player?.last_name || ""}
                </Badge>
                <Badge bg="info">{selectedCell.windowIdx === -1 ? "Start" : selectedWin?.label}</Badge>
                {selectedActivePos && (
                  <Badge bg={selectedActivePos.approved ? "success" : "warning"}>
                    {formatPos(selectedActivePos.name, selectedActivePos.side)}
                    {selectedActivePos.approved ? "" : " (auto)"}
                  </Badge>
                )}
              </>
            )}
          </div>

          {playerView === "formation" ? (
            <div className="d-flex justify-content-center">
              <FormationPitchSelector
                formationName={fpFormationName}
                formationsCatalog={formationsCatalog}
                teamSupertracks={fpTeamSupertracks}
                positionsByUid={fpPositionsByUid}
                selectedUid={selectedCell?.uid ?? null}
                onSelect={(uid) => handleCellClick(uid, selectedCell?.windowIdx ?? -1)}
                onAssign={(name, side, displacedUid, code) => {
                  if (!selectedCell) return;
                  applyPositionChange(selectedCell.uid, selectedCell.windowIdx, name, side, code);
                  if (displacedUid) clearPositionChange(displacedUid, selectedCell.windowIdx);
                }}
                onClear={() => {
                  if (selectedCell) clearPositionChange(selectedCell.uid, selectedCell.windowIdx);
                }}
              />
            </div>
          ) : videoSrc && selectedCell ? (
            <>
              <div className="video-info mb-1 d-flex align-items-center gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleEditOpen}
                  style={{ fontSize: 11, padding: "1px 8px" }}
                >
                  <FontAwesomeIcon icon={["fas", "edit"]} /> Edit
                </Button>
                {selectedActivePos && !selectedActivePos.approved && (
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => handleApprove(selectedCell.uid, selectedCell.windowIdx)}
                    style={{ fontSize: 11, padding: "1px 8px" }}
                  >
                    <FontAwesomeIcon icon={["fas", "check"]} /> Approve
                  </Button>
                )}
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={toggleFullscreen}
                  style={{ fontSize: 11, padding: "1px 8px", marginLeft: "auto" }}
                  title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                >
                  <FontAwesomeIcon icon={["fas", isFullscreen ? "compress" : "expand"]} />
                </Button>
              </div>
              <VideoPlayer
                ref={videoRef}
                src={videoSrc}
                fps={fps}
                frameStep={frameStep}
                startTime={selectedWin?.startSec}
                endTime={selectedWin?.endSec}
                tracks={videoTracks}
                colors={videoColors}
                idVisible={true}
              />
            </>
          ) : (
            <div className="video-placeholder d-flex align-items-center justify-content-center text-muted">
              <div className="text-center">
                <FontAwesomeIcon icon={["fas", "video"]} size="2x" className="mb-2" />
                <div>Click a cell to preview video</div>
              </div>
            </div>
          )}

          {/* Position pitch map */}
          {(() => {
            // For substitutes (start_lineup=false) when Start cell is selected,
            // jump to first window where they have data. Otherwise use selectedWin.
            let pitchWin = selectedWin;
            if (selectedCell && selectedCell.windowIdx === -1) {
              const sel = data.supertracks.find((s) => s.super_track_uid === selectedCell.uid);
              if (sel && sel.start_lineup === false) {
                const firstWinWithData = selectedUidWindows.find((w) =>
                  hasWeightedPosition(data.weighted_positions, sel.kind, sel.super_track_uid, w.startSec, w.endSec)
                );
                if (firstWinWithData) pitchWin = firstWinWithData;
              }
            }

            let computedStartMinute, computedEndMinute;
            if (pitchMode === "match") {
              computedStartMinute = 1;
              computedEndMinute = effectiveTimeMin;
            } else if (pitchRange) {
              computedStartMinute = Math.floor(pitchRange.startSec / 60) + 1;
              computedEndMinute = Math.max(computedStartMinute, Math.ceil(pitchRange.endSec / 60));
            } else {
              computedStartMinute = pitchMinute + 1;
              computedEndMinute = pitchMinute + pitchWindowMin;
            }

            const pitchTs = pitchRange ? pitchRange.startSec : pitchMinute * 60;
            const matchTimeSec = (effectiveTimeMin) * 60;
            const teamWins = selectedSt ? (windowsByTeam[selectedSt.kind] || []) : [];
            const teamRuns = selectedSt ? (formationsTimeline[selectedSt.kind] || []) : [];
            const viewStartSec = pitchRange ? pitchRange.startSec : pitchMinute * 60;
            const viewEndSec = pitchRange ? pitchRange.endSec : Math.min((pitchMinute + pitchWindowMin) * 60, matchTimeSec);

            let positionPitchSts = data.supertracks;
            if (selectedSt) {
              const tk = selectedSt.kind;
              const teamAll = data.supertracks.filter((s) => s.kind === tk);
              const withWP = teamAll.filter((s) =>
                hasWeightedPosition(data.weighted_positions, tk, s.super_track_uid, viewStartSec, viewEndSec)
              );
              if (withWP.length > playersPerTeam) {
                const prev = withWP.filter((s) =>
                  hasWeightedPosition(data.weighted_positions, tk, s.super_track_uid, 0, viewStartSec)
                );
                const fresh = withWP.filter((s) =>
                  !hasWeightedPosition(data.weighted_positions, tk, s.super_track_uid, 0, viewStartSec)
                );
                const sl = Math.max(0, playersPerTeam - prev.length);
                const onField = new Set([...prev, ...fresh.slice(0, sl)].map((s) => s.super_track_uid));
                positionPitchSts = data.supertracks.filter((s) => s.kind !== tk || onField.has(s.super_track_uid));
              }
            }

            return (
          <div className="mt-2">
            <PositionPitch
              supertracks={positionPitchSts}
              selectedUid={selectedCell?.uid ?? null}
              weightedPositions={data.weighted_positions}
              fieldSize={data.field_size}
              startMinute={computedStartMinute}
              endMinute={computedEndMinute}
              phase={pitchPhase}
              getStartPosition={(uid) => {
                const positions = getEffectivePositions(uid);
                const ts = pitchTs;
                let best = null;
                for (const p of positions) {
                  if (p.name && (p.timestamp ?? 0) <= ts) best = p;
                }
                return best;
              }}
              dynamicWindows={teamWins}
              formationRuns={teamRuns}
              matchTimeSec={matchTimeSec}
              viewStartSec={viewStartSec}
              viewEndSec={viewEndSec}
            />
            <div className="mt-2 d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
              <span className="text-muted">{pitchMinute}'</span>
              <div style={{ flex: 1, position: "relative" }}>
                {teamWins.map((w, i) => {
                  const pct = (w.startSec / matchTimeSec) * 100;
                  return (
                    <div key={i} style={{ position: "absolute", left: `${pct}%`, top: -2, pointerEvents: "none", zIndex: 2, transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <span style={{ fontSize: 8, color: "#f59e0b", fontWeight: 600, lineHeight: 1 }}>{w.label}</span>
                      <div style={{ width: 1, height: 20, background: "#f59e0b", opacity: 0.6 }} />
                    </div>
                  );
                })}
                <input
                  type="range"
                  min={0}
                  max={effectiveTimeMin}
                  value={pitchMinute}
                  onChange={(e) => { setPitchMinute(Number(e.target.value)); setPitchRange(null); }}
                  style={{ width: "100%", position: "relative", zIndex: 1, marginTop: 10 }}
                />
              </div>
              <span className="text-muted">{effectiveTimeMin}'</span>
            </div>
            <div className="mt-1 d-flex align-items-center gap-1" style={{ fontSize: 11 }}>
              <span className="text-muted">Phase:</span>
              <div className="btn-group btn-group-sm">
                {["attack", "defense", "common"].map((ph) => (
                  <Button
                    key={ph}
                    variant={pitchPhase === ph ? "primary" : "outline-primary"}
                    onClick={() => setPitchPhase(ph)}
                    style={{ fontSize: 10, padding: "1px 6px" }}
                  >
                    {ph === "attack" ? "Att" : ph === "defense" ? "Def" : "All"}
                  </Button>
                ))}
              </div>
              <span className="text-muted ms-2">Window:</span>
              <input
                type="number"
                min={1}
                max={10}
                value={pitchWindowMin}
                onChange={(e) => setPitchWindowMin(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
                style={{ width: 42, fontSize: 11, padding: "1px 4px", textAlign: "center" }}
              />
              <span className="text-muted">min</span>
              <Button
                variant="outline-secondary"
                size="sm"
                className="ms-auto"
                style={{ fontSize: 10, padding: "1px 6px" }}
                onClick={() => setAddFormationModal({ teamKind: selectedSt?.kind || "left_team", defaultMinute: pitchMinute || 0 })}
              >
                + Formation
              </Button>
            </div>
          </div>
            );
          })()}
        </div>
      </div>

      <PositionEditModal
        show={!!editModal}
        onHide={() => setEditModal(null)}
        currentPos={editModal?.currentPos}
        onSave={handleEditSave}
        onClear={handleEditClear}
        formationsCatalog={formationsCatalog}
        {...(editModal && (() => {
          const editSt = data.supertracks.find((s) => s.super_track_uid === editModal.uid);
          if (!editSt) return {};

          if (editModal.windowIdx === -1) {
            if (editSt.start_lineup !== true) return {};
            const teamFormation = editSt.kind === "left_team" ? formationLeft : formationRight;
            return { formationName: teamFormation };
          }

          const editPositions = getEffectivePositions(editModal.uid);
          const startPos = editPositions.find((p) => (p.timestamp ?? 0) === 0) || null;
          const editWindows = getWindowsForUid(editModal.uid);
          const editWin = editWindows[editModal.windowIdx];
          const activeFormationName = editWin
            ? getActiveFormation(formationsTimeline, editSt.kind, editWin.startSec)
            : null;
          return { startPos, isWindowEdit: true, formationName: activeFormationName };
        })())}
      />

      <AddFormationModal
        show={!!addFormationModal}
        onHide={() => setAddFormationModal(null)}
        onSave={(minute, formation) => handleAddFormation(addFormationModal.teamKind, minute, formation)}
        formations={formationsCatalog}
        defaultMinute={addFormationModal?.defaultMinute}
      />

      <PlayerBindModal
        show={!!bindModal}
        onHide={() => { setBindModal(null); setPlayerSearch(""); }}
        players={bindModal ? getPlayersForBind(bindModal.kind) : []}
        onBind={handlePlayerBind}
        onCreate={handleCreateAndBind}
        search={playerSearch}
        setSearch={setPlayerSearch}
        loading={!!bindingUid}
        supertrackJersey={bindModal ? data.supertracks.find((s) => s.super_track_uid === bindModal.uid)?.jersey : null}
      />
    </div>
  );
};

export default VerifyPositions;
